import { useRef, useState } from 'react';
import _ from 'lodash';
import T from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import ContactResource from 'App/resources/Contact';
import ContactRelationResource from 'App/resources/ContactRelation';
import EmailDuplicatesResource from 'Resources/EmailDuplicates';
import EmailSuggestResource from 'Resources/EmailSuggest';
import ValidationService from 'Services/ValidationService';
import { checkUnbounceContact, NOT_ABLE_TO_DEBOUNCE_REASONS } from 'Helpers/bounce';
import { compressChanges } from '../ListView/ListViewHelpers';
import type Contact from 'App/resources/Model/Contact';
import { FormObserverOnFieldChange } from '../FormObserver/FormObserver';
import { MAX_INLINE_DUPLICATES, MappedContact } from './editContactHelpers';
import { getBlockingDuplicate, partitionDuplicates } from './blockingDuplicates';
import { openEditContact } from './openEditContact';
import AppService from 'App/legacy/AppService';
import StateRouter from 'App/services/StateRouter';

type Args = {
	isEdit: boolean;
	close: (contact?: Contact) => void;
};

/**
 * Encapsulates the contact email side-effects: duplicate detection (same-client
 * + cross-company), the save-blocking rule, email suggestion, and unbounce.
 */
const useEditContactEmail = ({ isEdit, close }: Args) => {
	const customerId = AppService.getCustomerId();

	const [suggestEmail, setSuggestEmail] = useState('');
	const [showSuggestion, setShowSuggestion] = useState(false);
	const [showSuggestionWarning, setShowSuggestionWarning] = useState(false);
	const [suggestionDuplicates, setSuggestionDuplicates] = useState<Contact[]>([]);
	const [selectedDuplicate, setSelectedDuplicate] = useState<Contact | null>(null);
	const [clientSuggestionDuplicates, setClientSuggestionDuplicates] = useState<Contact[]>([]);
	const [crossCompanySuggestionDuplicates, setCrossCompanySuggestionDuplicates] = useState<Contact[]>([]);
	const [duplicateEmailMessage, setDuplicateEmailMessage] = useState('');
	const [crossCompanyDuplicateEmailMessage, setCrossCompanyDuplicateEmailMessage] = useState('');
	const [connectingContact, setConnectingContact] = useState(false);

	const [originalEmail, setOriginalEmail] = useState('');
	const [mailBounces, setMailBounces] = useState<Contact['mailBounces']>([]);
	const [showUnBounce, setShowUnBounce] = useState(false);
	const [unBouncing, setUnBouncing] = useState(false);
	const [unBounceTooltip, setUnBounceTooltip] = useState('');

	const validateEmail = useRef(
		_.debounce(async (client: MappedContact['client'], contactId: number | undefined, email: string) => {
			if (!ValidationService.validEmail(email)) {
				return;
			}
			setSelectedDuplicate(null);
			let data: Contact[] = [];
			try {
				data = client ? await EmailDuplicatesResource.findDuplicates(client.id, contactId, email) : [];
			} catch (e) {
				logError(e as Error, 'Failed to get email duplicates');
				return;
			}

			if (data.length >= 1 && data.length <= MAX_INLINE_DUPLICATES) {
				try {
					data = await Promise.all(data.map(d => ContactResource.get(d.id).then(r => r.data)));
				} catch (e) {
					logError(e as Error, 'Failed to load duplicate contacts');
				}
			}

			setShowSuggestionWarning(!!data.length);
			setSuggestionDuplicates(data);

			const [clientDuplicates, crossCompanyDuplicates] = partitionDuplicates(data, client?.id);
			setClientSuggestionDuplicates(clientDuplicates);
			setCrossCompanySuggestionDuplicates(crossCompanyDuplicates);
			setDuplicateEmailMessage(
				clientDuplicates.length === 1
					? T('contact.saveDuplicateEmail.tooltip')
					: T('contact.saveDuplicateEmails.tooltip', { label: T('contact.viewContactsWithSuchEmail') })
			);
			setCrossCompanyDuplicateEmailMessage(
				crossCompanyDuplicates.length === 1
					? T('contact.saveCrossCompanyDuplicateEmail.tooltip')
					: T('contact.saveCrossCompanyDuplicateEmails.tooltip', {
							label: T('contact.viewContactsWithSuchEmail')
					  })
			);
			if (data.length === 1) {
				setSelectedDuplicate(data[0]);
			}
		}, 500)
	).current;

	const suggestEmailFn = useRef(
		_.debounce((clientId: number, firstName: string, lastName: string, currentEmail: string) => {
			EmailSuggestResource.suggest(clientId, firstName, lastName)
				.then(suggested => {
					setSuggestEmail(suggested ?? '');
					if (suggested && currentEmail !== suggested) {
						setShowSuggestion(true);
					}
				})
				.catch((e: Error) => logError(e, 'Error fetching suggested email'));
		}, 500)
	).current;

	const getSuggestion = (
		client: MappedContact['client'],
		firstName: string,
		lastName: string,
		currentEmail: string
	) => {
		if (!AppService.getSelf()?.userParams?.emailSuggestions) {
			return;
		}
		if (client?.id && firstName && lastName) {
			suggestEmailFn(client.id, firstName, lastName, currentEmail);
		}
	};

	const insertSuggestEmail = (onFormChange: FormObserverOnFieldChange) => {
		onFormChange('email', suggestEmail);
		setShowSuggestion(false);
		setClientSuggestionDuplicates([]);
		setShowSuggestionWarning(false);
		setTimeout(() => setSuggestEmail(''), 1000);
	};

	const initEmailState = (loaded: Partial<Contact>) => {
		setOriginalEmail(loaded.email ?? '');
		setMailBounces(loaded.mailBounces ?? []);
		const { valid, reason } = checkUnbounceContact(loaded as Contact);
		setShowUnBounce(valid || reason === NOT_ABLE_TO_DEBOUNCE_REASONS.MAX_REMOVALS_REACHED);
		setUnBounceTooltip(
			reason === NOT_ABLE_TO_DEBOUNCE_REASONS.MAX_REMOVALS_REACHED
				? T('email.unbounce.maxNumberRemovalsReached')
				: ''
		);
		validateEmail(
			loaded.client ? { id: loaded.client.id, name: loaded.client.name } : null,
			loaded.id,
			loaded.email ?? ''
		);
	};

	const onUnBounceContact = (email: string) => {
		if (!email) {
			return;
		}
		setUnBouncing(true);
		const contactResource = getAngularModule('Contact') as {
			unbounce: (contact: { email: string; mailBounces: Contact['mailBounces'] }) => Promise<unknown>;
		};
		contactResource
			.unbounce({ email, mailBounces })
			.then(() => {
				setMailBounces(mailBounces.filter(b => b !== 'hard_bounce'));
				setUnBouncing(false);
				setShowUnBounce(false);
			})
			.catch((error: { status?: number }) => {
				if (!error?.status || error.status >= 500) {
					logError(error as Error, 'EditContact - onUnBounceContact');
				}
				setUnBouncing(false);
			});
	};

	const getContactLink = (target: Contact) => StateRouter.href('contact.dashboard', { customerId, id: target.id });
	const getCompanyLink = (target: Contact) =>
		StateRouter.href('account.dashboard', { customerId, id: target.client?.id });
	const getFilteredListLink = (email: string) => {
		const filters = {
			Email: { filterName: 'Email', comparisonType: 'Search', inactive: false, value: email, type: 'text' }
		};
		return StateRouter.href('react-root-contacts', {
			customerId,
			f: encodeURIComponent(compressChanges({ filters } as unknown as Parameters<typeof compressChanges>[0]))
		});
	};

	const hasInitialEmailChanged = (email: string) => isEdit && !!originalEmail && originalEmail !== email;

	const getBlockingDuplicateMessage = (email: string): string | null => {
		if (isEdit && !hasInitialEmailChanged(email)) {
			return null;
		}
		const blocking = getBlockingDuplicate(clientSuggestionDuplicates, crossCompanySuggestionDuplicates);
		if (!blocking) {
			return null;
		}
		return blocking.type === 'sameCompany' ? duplicateEmailMessage : crossCompanyDuplicateEmailMessage;
	};

	const editDuplicatedContact = (contactId: number) => {
		close(undefined);
		openEditContact({ id: contactId });
	};

	const connectExistingContact = async (duplicate: Contact, client: MappedContact['client']) => {
		if (connectingContact || !client?.id || !duplicate?.id) {
			return;
		}
		setConnectingContact(true);
		try {
			const alreadyConnected = duplicate.client && duplicate.client.id === client.id;
			if (!alreadyConnected) {
				await ContactRelationResource.save({ contactId: duplicate.id, relatedToClientId: client.id });
			}
			close({ ...duplicate, client: client as Contact['client'] });
		} catch (e) {
			logError(e as Error, 'Failed to connect existing contact');
			setConnectingContact(false);
		}
	};

	return {
		mailBounces,
		originalEmail,
		showUnBounce,
		unBouncing,
		unBounceTooltip,
		onUnBounceContact,
		suggestEmail,
		showSuggestion,
		getSuggestion,
		insertSuggestEmail,
		showSuggestionWarning,
		suggestionDuplicates,
		selectedDuplicate,
		setSelectedDuplicate,
		connectingContact,
		getContactLink,
		getCompanyLink,
		getFilteredListLink,
		getBlockingDuplicateMessage,
		editDuplicatedContact,
		connectExistingContact,
		validateEmail,
		initEmailState
	};
};

export type EditContactEmail = ReturnType<typeof useEditContactEmail>;

export default useEditContactEmail;
